import React, { useState } from 'react';
import './WorkflowGuide.css';

const WorkflowGuide = ({ show, onClose, currentContext = 'general' }) => {
  const [activeTab, setActiveTab] = useState(currentContext);

  const workflows = {
    general: {
      title: '🗺️ Guide général d\'analyse',
      description: 'Comprendre quand et comment utiliser chaque type d\'analyse',
      steps: [
        {
          id: 'assessment',
          title: 'Évaluation du contexte',
          icon: '🎯',
          description: 'Identifiez le type de modification et son impact potentiel',
          details: [
            'Nouveau nœud → Analyse de schéma recommandée',
            'Modification simple → Mise à jour directe possible',
            'Modification critique → Analyse d\'impact obligatoire',
            'Nœud sensible (API, DB, Auth) → Analyse systématique'
          ]
        },
        {
          id: 'analysis',
          title: 'Choix de l\'analyse',
          icon: '🔍',
          description: 'Sélectionnez le bon type d\'analyse selon le contexte',
          details: [
            'Analyse de schéma: Comprendre la structure et les relations',
            'Analyse d\'impact: Évaluer les risques et dépendances',
            'Génération de tests: Créer des scénarios de validation',
            'Recherche vectorielle: Trouver des éléments similaires'
          ]
        },
        {
          id: 'validation',
          title: 'Validation et application',
          icon: '✅',
          description: 'Examinez les résultats et appliquez les modifications',
          details: [
            'Examinez les nœuds et relations détectés',
            'Validez les risques identifiés',
            'Intégrez les tests générés si pertinents',
            'Appliquez les modifications en toute sécurité'
          ]
        }
      ]
    },
    creation: {
      title: '✨ Création de nœud',
      description: 'Workflow optimisé pour créer de nouveaux nœuds',
      steps: [
        {
          id: 'define',
          title: 'Définition',
          icon: '📝',
          description: 'Définissez les caractéristiques du nouveau nœud',
          details: [
            'Nom unique et descriptif',
            'Type approprié (Feature, API, Service, etc.)',
            'Description détaillée du rôle',
            'Technologies utilisées'
          ]
        },
        {
          id: 'analyze',
          title: 'Analyse préalable',
          icon: '🔍',
          description: 'Analysez le schéma pour détecter les liens potentiels',
          details: [
            'Détection automatique des relations',
            'Suggestions de nœuds liés',
            'Identification des patterns',
            'Validation de la cohérence'
          ]
        },
        {
          id: 'preview',
          title: 'Prévisualisation',
          icon: '👁️',
          description: 'Examinez le nœud avant création définitive',
          details: [
            'Vérifiez les métadonnées',
            'Validez les relations suggérées',
            'Ajustez si nécessaire',
            'Confirmez la création'
          ]
        }
      ]
    },
    modification: {
      title: '🛡️ Modification avec Analyse d\'Impact IA',
      description: 'Workflow sécurisé pour modifier des nœuds avec rapport complet',
      steps: [
        {
          id: 'select',
          title: 'Sélection et Évaluation',
          icon: '🎯',
          description: 'Sélectionnez le nœud et évaluez automatiquement les risques',
          details: [
            'Sélection du nœud via recherche ou graphe',
            'Détection automatique du niveau de criticité',
            'Évaluation des changements proposés',
            'Calcul du score de risque en temps réel'
          ]
        },
        {
          id: 'impact',
          title: 'Analyse d\'Impact IA Complète',
          icon: '🤖',
          description: 'L\'IA génère un rapport détaillé avec tests et conseils',
          details: [
            '📊 Rapport d\'impact avec score de confiance',
            '🔗 Analyse des dépendances et nœuds impactés',
            '🧪 Génération automatique de tests de non-régression',
            '💡 Recommandations et conseils d\'amélioration',
            '⚠️ Identification des risques potentiels',
            '🎯 Scénarios de validation Gherkin'
          ]
        },
        {
          id: 'validate',
          title: 'Validation et Application',
          icon: '✅',
          description: 'Examinez le rapport et appliquez en toute sécurité',
          details: [
            'Lecture du rapport d\'impact généré',
            'Validation des tests proposés par l\'IA',
            'Intégration des tests dans votre suite',
            'Application sécurisée des modifications',
            'Suivi des métriques d\'impact'
          ]
        }
      ]
    },
    simple_update: {
      title: '⚡ Mise à jour Simple (Sans IA)',
      description: 'Modification rapide sans analyse - À utiliser avec précaution',
      steps: [
        {
          id: 'caution',
          title: 'Évaluation des Risques',
          icon: '⚠️',
          description: 'Vérifiez manuellement que les modifications sont sûres',
          details: [
            'Modifications mineures uniquement (description, statut)',
            'Pas de changement de type ou de nom',
            'Nœud non critique (pas d\'API, DB, Auth)',
            'Aucune dépendance complexe connue'
          ]
        },
        {
          id: 'apply',
          title: 'Application Directe',
          icon: '🚀',
          description: 'Mise à jour immédiate sans validation IA',
          details: [
            'Aucun rapport d\'impact généré',
            'Aucun test automatique créé',
            'Aucune validation des dépendances',
            'Responsabilité manuelle des conséquences'
          ]
        },
        {
          id: 'monitor',
          title: 'Surveillance Manuelle',
          icon: '👁️',
          description: 'Surveillez manuellement les impacts',
          details: [
            'Vérifiez les relations après modification',
            'Testez manuellement les fonctionnalités liées',
            'Surveillez les erreurs dans les logs',
            'Validez avec l\'équipe si nécessaire'
          ]
        }
      ]
    }
  };

  const currentWorkflow = workflows[activeTab];

  if (!show) return null;

  return (
    <div className="workflow-guide-overlay">
      <div className="workflow-guide-modal">
        <div className="workflow-guide-header">
          <h3 className="guide-title">
            <span className="guide-icon">🧭</span>
            Guide d'analyse intelligente
          </h3>
          <button className="close-btn" onClick={onClose}>
            <span>×</span>
          </button>
        </div>

        <div className="workflow-guide-tabs">
          {Object.entries(workflows).map(([key, workflow]) => (
            <button
              key={key}
              className={`tab-btn ${activeTab === key ? 'active' : ''} ${key === 'simple_update' ? 'tab-warning' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {workflow.title}
            </button>
          ))}
        </div>

        <div className="workflow-guide-content">
          <div className="workflow-header">
            <h4 className="workflow-title">{currentWorkflow.title}</h4>
            <p className="workflow-description">{currentWorkflow.description}</p>
          </div>

          <div className="workflow-steps">
            {currentWorkflow.steps.map((step, index) => (
              <div key={step.id} className="workflow-step">
                <div className="step-header">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-icon">{step.icon}</div>
                  <div className="step-info">
                    <h5 className="step-title">{step.title}</h5>
                    <p className="step-description">{step.description}</p>
                  </div>
                </div>
                <div className="step-details">
                  <ul className="details-list">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="detail-item">
                        <span className="detail-bullet">•</span>
                        <span className="detail-text">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="workflow-guide-footer">
            <div className="best-practices">
              <h5 className="practices-title">
                <span className="practices-icon">💡</span>
                Bonnes pratiques
              </h5>
              <div className="practices-grid">
                <div className="practice-item">
                  <span className="practice-icon">🛡️</span>
                  <span className="practice-text">Toujours utiliser l'analyse IA pour les nœuds critiques (API, DB, Auth)</span>
                </div>
                <div className="practice-item">
                  <span className="practice-icon">�</span>
                  <span className="practice-text">Examiner le rapport d'impact et le score de confiance</span>
                </div>
                <div className="practice-item">
                  <span className="practice-icon">🧪</span>
                  <span className="practice-text">Intégrer les tests générés automatiquement dans votre suite</span>
                </div>
                <div className="practice-item">
                  <span className="practice-icon">⚡</span>
                  <span className="practice-text">N'utiliser la mise à jour simple que pour des changements mineurs</span>
                </div>
                <div className="practice-item">
                  <span className="practice-icon">🔍</span>
                  <span className="practice-text">Vérifier les dépendances identifiées par l'IA</span>
                </div>
                <div className="practice-item">
                  <span className="practice-icon">📈</span>
                  <span className="practice-text">Utiliser les métriques d'impact pour prendre des décisions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowGuide;
